// Movers: Groww-style ranked lists (strongest bullish/bearish, sweeps, volume, order flow).
import { useRouter, useSearchParams } from '@/lib/nav';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NSE, forSector, snapshot } from '@planb/core';
import { lastBarForming, todayIso, useIndexStrip, useMarket } from '@/lib/client/use-market';
import { MOVER_LISTS, moverList, type MoverList } from '@/lib/client/movers';
import { liveSweep } from '@/charts/board';
import { usePageTools } from '@/lib/desk/tools';
import { Button } from '@/components/ui/button';
import { openDesk } from './desk';
import { useMe } from '@/lib/client/use-me';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppHeader, Page } from './app-header';
import { IndexSelect, carry, dateSeed, indexById, stockPath } from './common';
import { Pills, DeskIcon } from './market-ui';
import { MoversTable } from './movers-table';

const ALL = '__all';

export function MoversApp() {
  const router = useRouter();
  const params = useSearchParams();
  const [index, setIndex] = useState(indexById(params.get('index'))?.id ?? 'FNO');
  // Without ?index=, open on the member's default index once their preferences load.
  const prefIndex = useMe()?.prefs.index;
  const usedPref = useRef(false);
  useEffect(() => {
    if (usedPref.current || !prefIndex || params.get('index') || !indexById(prefIndex)) return;
    usedPref.current = true;
    setIndex(prefIndex);
  }, [prefIndex, params]);
  const [list, setList] = useState<MoverList>((MOVER_LISTS.find((m) => m.id === params.get('list'))?.id) ?? 'bulls');
  const [sector, setSector] = useState(params.get('sector') ?? ALL);
  const seed = Number(params.get('seed')) || dateSeed();

  const { session, status, mode, live, version, user, role } = useMarket({ index, seed });
  const strip = useIndexStrip(session, live, version);
  const ix = indexById(index)!;
  const snap = useMemo(() => {
    if (!session) return null;
    const members = new Set(ix.members);
    const board = { ...session, stocks: session.stocks.filter((s) => members.has(s.symbol)) };
    const n = board.stocks[0]?.bars.length ?? 0;
    return n ? snapshot(NSE, board, n, { lastBarForming: lastBarForming(mode, n, n) }) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, ix, version, mode]);
  const activeSector = sector !== ALL && snap?.sectors.some((s) => s.name === sector) ? sector : null;
  const view = snap && activeSector ? forSector(NSE, snap, activeSector) : snap;
  const rows = view ? moverList(view, list) : [];
  const query = carry({ index, live, seed });
  const meta = MOVER_LISTS.find((m) => m.id === list)!;

  useEffect(() => {
    const q = new URLSearchParams(query.slice(1));
    if (list !== 'bulls') q.set('list', list);
    if (activeSector) q.set('sector', activeSector);
    router.replace(q.size ? `/movers?${q}` : '/movers', { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, list, activeSector]);

  // ---- The desk's view of this page: the list, filters and rows exactly as the table shows them.
  const question: Record<MoverList, (sym?: string) => string> = {
    bulls: (x) => x ? `Why is ${x} among the strongest bullish movers today, and is it in a tested setup?` : `Explain today's strongest bullish movers in ${ix.name}: what they have in common, and which are in a tested setup.`,
    bears: (x) => x ? `Why is ${x} among the strongest bearish movers today?` : `Explain today's strongest bearish movers in ${ix.name}: what they have in common.`,
    sweeps: (x) => x ? `What does the sweep in ${x} mean, and how did sweeps do in MoneyHQ's tests?` : `Explain today's fresh sweeps in ${ix.name} and what MoneyHQ's tests found about sweeps.`,
    volume: (x) => x ? `Why is ${x} a volume shocker today?` : `Explain today's volume shockers in ${ix.name}: which are rising, which falling, and which are in a tested setup.`,
    flow: (x) => x ? `What does the order flow in ${x} say today?` : `Explain today's order-flow leaders in ${ix.name}.`,
  };
  usePageTools({
    read_page: () => {
      if (!snap) return { page: 'movers', note: 'still loading' };
      const n2 = (x: number) => +x.toFixed(2);
      return {
        page: 'movers', list: meta.label, listMeans: meta.blurb, index: ix.name, sector: activeSector ?? 'all sectors', session: session?.date, asOf: snap.time,
        data: live ? (mode === 'closed' ? 'real NSE data, market closed' : 'real NSE data, live') : 'loading',
        stocksInList: rows.length,
        rows: rows.slice(0, 25).map((s, i) => {
          const sw = liveSweep(s, snap.t);
          return {
            rank: i + 1, symbol: s.symbol, sector: s.sector, price: s.ltp, changePct: n2(s.pct), strength: s.score, bias: s.bull ? 'bullish' : 'bearish',
            volumeVsUsualForTimeOfDay: `${s.relVol.toFixed(2)}×`, orderFlowDeltaPct: s.flow ? n2(s.flow.deltaPct) : null,
            futuresBuildUp: s.hasOI ? s.buildUp : null, withSector: s.withSector, eventRisk: s.eventRisk || undefined,
            sweep: sw ? { side: sw.type === 'bear' ? 'poked above a high, closed back below' : 'poked below a low, closed back above', level: sw.name, price: sw.level } : undefined,
          };
        }),
        note: 'Lists rank what happened today; none of them is a tested signal (MoneyHQ\'s intraday tests found no edge after costs).',
      };
    },
  });
  const askList = () => openDesk(question[list]());

  return (
    <>
      <AppHeader section="movers" mode={mode} status={status} user={user} role={role} query={query} strip={strip} activeIndex={index}
        onSelectIndex={(id) => { setIndex(id); setSector(ALL); }} />
      <Page>
        <h1 className="text-[26px] font-semibold">Movers</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{session?.date || todayIso()} · {snap ? `as of ${snap.time}` : 'loading…'} · ranked within {ix.name}</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <IndexSelect value={index} onChange={(id) => { setIndex(id); setSector(ALL); }} />
          <Select value={activeSector ?? ALL} onValueChange={setSector}>
            <SelectTrigger aria-label="Sector" className="h-9 min-w-40 rounded-full bg-card font-medium"><SelectValue /></SelectTrigger>
            <SelectContent position="popper" className="max-h-96">
              <SelectItem value={ALL}>All sectors</SelectItem>
              {snap?.sectors.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} <span className="text-muted-foreground">({s.adv}/{s.n} up)</span></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Pills label="Mover list" value={list} onChange={setList} options={MOVER_LISTS.map((m) => ({ id: m.id, label: m.label }))} className="mt-4" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-[13px] text-muted-foreground">{meta.blurb} <span className="text-ink">{rows.length} stocks.</span></p>
          {rows.length > 0 && <Button variant="ghost" size="sm" className="ml-auto h-7 gap-1 text-[12px] text-brand-strong" onClick={askList}><DeskIcon className="size-3.5" />Ask about this list</Button>}
        </div>

        <Card className="mt-3 gap-0 py-0">
          <MoversTable rows={rows} t={snap?.t ?? 0} href={(s) => stockPath(s, query)} onAsk={(s) => openDesk(question[list](s.symbol))}
            empty={list === 'flow' ? 'Order flow is measured from live ticks, so it appears once the live feed is running.' : undefined} />
        </Card>
        <p className="mt-4 text-xs text-muted-foreground/80">For learning only, not investment advice. Lists describe what has happened today; they are not recommendations.</p>
      </Page>
    </>
  );
}
